
import React, { useRef, useState } from 'react';
import { SITE_CONTENT } from '../constants/content';
import { Page } from '../types';

interface DownloadSectionProps {
  navigate?: (page: Page) => void;
}

const DownloadSection: React.FC<DownloadSectionProps> = ({ navigate }) => {
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [hpField, setHpField] = useState('');
  const [platform, setPlatform] = useState<'android' | 'ios' | 'both'>('android');
  const [consent, setConsent] = useState(false);
  const [newsletterConsent, setNewsletterConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(6).fill(''));
  const [codeSubmitted, setCodeSubmitted] = useState<boolean>(false);
  const verificationRefs = useRef<Array<HTMLInputElement | null>>([]);

  const validateEmail = (email: string) => {
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!email || email.trim() === '') return 'Bitte gib deine E-Mail-Adresse ein.';
    if (!regex.test(email)) return 'Das Format der E-Mail-Adresse ist ungültig.';
    return '';
  };

  const handleWaitlistSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    if (!consent) {
      newErrors.consent = 'Bitte stimme der Datenschutzerklärung zu.';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      // send data and request a confirmation code via email
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, name, platform, hp_field: hpField, newsletter: newsletterConsent
        })
      });

      if (!res.ok) return setErrors({ form: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut, oder wende dich an den Support.' });
      setWaitlistSubmitted(true);
      setErrors({});
    } catch {
      setErrors({ form: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut, oder wende dich an den Support.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    if (errors[field] || errors.form) {
      const updatedErrors = { ...errors };
      delete updatedErrors[field];
      delete updatedErrors.form;
      setErrors(updatedErrors);
    }
    
    if (field === 'email') setEmail(value as string);
    if (field === 'name') setName(value as string);
    if (field === 'consent') setConsent(value as boolean);
  };

  const handleVerificationSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      const res = await fetch('/api/waitlist-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authCode: parseInt(verificationCode.join('')), email })
      });

      if (!res.ok) return setErrors({ form: 'Verifizierung nicht möglich. Bitte probiere es erneut, oder wende dich an den Support.' });
      setCodeSubmitted(true);
      setErrors({});
      setVerificationCode(Array(6).fill(''));
    } catch {
      setErrors({ form: 'Interner Fehler. Bitte probiere es erneut, oder wende dich an den Support.' });
    }
    finally {
      setIsLoading(false)
    }
  };

  const handleVerificationInputChange = (index: number, rawValue: string) => {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    const nextCode = [...verificationCode];
    nextCode[index] = digit;
    setVerificationCode(nextCode);

    if (digit && index < 5) {
      verificationRefs.current[index + 1]?.focus();
    }
  };

  const handleVerificationKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      verificationRefs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      verificationRefs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      verificationRefs.current[index + 1]?.focus();
    }
  };

  const handleVerificationPaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedDigits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6 - index);
    if (!pastedDigits) return;

    const nextCode = [...verificationCode];
    for (let i = 0; i < pastedDigits.length; i += 1) {
      nextCode[index + i] = pastedDigits[i];
    }
    setVerificationCode(nextCode);

    const nextFocusIndex = Math.min(index + pastedDigits.length, 5);
    verificationRefs.current[nextFocusIndex]?.focus();
  };

  return (
    <section className="container mx-auto px-4 md:px-6" aria-labelledby="download-title">
      <div className="bg-secondary rounded-[3rem] p-12 md:p-20 relative overflow-hidden text-white shadow-2xl">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary opacity-20 rounded-full -ml-32 -mt-32" aria-hidden="true"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-48 -mb-48" aria-hidden="true"></div>

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="text-center lg:text-left">
            <h2 id="download-title" className="text-4xl md:text-6xl font-black mb-8 leading-tight">
              Sei beim Start von <br className="hidden md:block"/> ErsteHilfe2Go dabei
            </h2>
            <p className="text-xl opacity-80 mb-12 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Trage dich jetzt unverbindlich in unsere Warteliste ein. Wir informieren dich rechtzeitig über den App-Launch.
            </p>
            
            <div className="flex flex-col gap-4 max-w-sm mx-auto lg:mx-0 text-sm opacity-60 italic" role="list">
               <span role="listitem">🔒 Sicher verschlüsselt</span>
               <span role="listitem">✅ Double-Opt-In Verfahren</span>
            </div>
          </div>

          <div className="bg-white p-8 md:p-12 rounded-[2.5rem] text-secondary shadow-2xl relative">
            {!waitlistSubmitted && (
              <>
                <h3 className="text-2xl font-black mb-8 flex items-center gap-3">
                   <span className="w-2 h-8 bg-primary rounded-full" aria-hidden="true"></span>
                   Anmeldung
                </h3>
                <form onSubmit={handleWaitlistSubmit} className="space-y-5" noValidate>
                  <input 
                    type="text" 
                    name="hp_field" 
                    className="hp-field" 
                    autoComplete="off" 
                    value={hpField}
                    onChange={(e) => setHpField(e.target.value)} 
                    tabIndex={-1} 
                    aria-hidden="true"
                  />

                  <div>
                    <label htmlFor="name-input" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">Dein Vorname (Optional)</label>
                    <input 
                      id="name-input"
                      type="text" 
                      placeholder="z.B. Max" 
                      value={name}
                      autoComplete="given-name"
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full bg-gray-100 border border-gray-100 p-4 rounded-xl text-secondary outline-none focus:ring-2 focus:ring-primary/30 transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="email-input" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">E-Mail Adresse *</label>
                    <input 
                      id="email-input"
                      type="email" 
                      required
                      aria-required="true"
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      autoComplete="email"
                      placeholder="beispiel@mail.de" 
                      value={email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full bg-gray-100 border p-4 rounded-xl text-secondary outline-none transition-all text-sm ${errors.email ? 'border-primary ring-2 ring-primary/20' : 'border-gray-100 focus:ring-2 focus:ring-primary/30'}`}
                    />
                    {errors.email && (
                      <p id="email-error" className="text-[10px] text-primary mt-1.5 font-bold" role="alert">{errors.email}</p>
                    )}
                  </div>

                  <fieldset>
                    <legend className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 block">Bevorzugte Plattform</legend>
                    <div className="grid grid-cols-3 gap-2">
                       {['android', 'ios', 'both'].map((id) => (
                          <button
                            key={id}
                            type="button"
                            onClick={() => setPlatform(id as 'android' | 'ios' | 'both')}
                            className={`py-3 rounded-xl text-[10px] font-bold transition-all border focus:ring-2 focus:ring-primary ${platform === id ? 'bg-secondary text-white border-secondary shadow-md' : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}
                            aria-pressed={platform === id}
                          >
                             {id === 'both' ? 'Beide' : id.toUpperCase()}
                          </button>
                       ))}
                    </div>
                  </fieldset>
                  
                  <div className="pt-2 space-y-4">
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <div className="mt-1">
                          <input 
                            type="checkbox" 
                            id="consent-check"
                            required
                            aria-required="true"
                            checked={consent}
                            onChange={(e) => handleInputChange('consent', e.target.checked)}
                            className="sr-only"
                          />
                          <div className={`w-6 h-6 rounded-lg border transition-all flex items-center justify-center ${consent ? 'bg-primary border-primary' : errors.consent ? 'bg-primary/5 border-primary' : 'bg-gray-100 border-gray-200'} group-focus-within:ring-2 group-focus-within:ring-primary/30`}>
                            {consent && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7"/></svg>}
                          </div>
                        </div>
                        <span className="text-[11px] text-gray-500 leading-snug">
                          Ich habe die <button type="button" onClick={() => navigate?.('datenschutz')} className="underline font-bold text-secondary hover:text-primary transition-colors">Datenschutzerklärung</button> gelesen und bin einverstanden.
                        </span>
                      </label>
                      {errors.consent && (
                        <p className="text-[10px] text-primary mt-1.5 font-bold pl-9" role="alert">{errors.consent}</p>
                      )}
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="mt-1">
                        <input 
                          type="checkbox" 
                          id="newsletter-check"
                          checked={newsletterConsent}
                          onChange={(e) => setNewsletterConsent(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-lg border transition-all flex items-center justify-center ${newsletterConsent ? 'bg-secondary border-secondary' : 'bg-gray-100 border-gray-200'} group-focus-within:ring-2 group-focus-within:ring-primary/30`}>
                          {newsletterConsent && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7"/></svg>}
                        </div>
                      </div>
                      <span className="text-[11px] text-gray-500 leading-snug">
                        Ja, ich möchte zusätzlich den freiwilligen Newsletter abonnieren.
                      </span>
                    </label>

                    {errors.form && (
                      <div className="p-4 bg-primary/10 rounded-2xl text-[11px] text-primary font-bold border border-primary/20" role="alert">
                        {errors.form}
                      </div>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    disabled={isLoading}
                    className="w-full bg-primary text-white py-5 rounded-2xl font-black text-xl hover:opacity-90 transition-all shadow-xl active:scale-[0.98] disabled:opacity-50 mt-4 flex items-center justify-center gap-3 focus:ring-4 focus:ring-primary/30"
                  >
                    {isLoading ? 'Verarbeite...' : SITE_CONTENT.waitlist.ctaForm}
                  </button>
                  <p className="text-[9px] text-gray-400 text-center leading-relaxed mt-4 font-medium" aria-hidden="true">
                    ✓ Kostenlos & unverbindlich • ✓ Kein Spam • ✓ Abmeldung jederzeit möglich
                  </p>
                  <p className="text-[9px] text-gray-400 text-center leading-relaxed mt-4 font-medium">
                    Hier geht es zur <a href="#abmelden"><u>Abmeldung</u></a>
                  </p>
                </form>
              </>
            )}
            {waitlistSubmitted && !codeSubmitted && (
              <div className="py-6 animate-fade-in" role="status" aria-live="polite">
                <h4 className="text-2xl font-black mb-3 text-secondary text-center">Bestätigungscode eingeben</h4>
                <p className="text-sm text-gray-500 text-center mb-7">
                  Wir haben dir einen 6-stelligen Code per E-Mail gesendet, bitte gib ihn hier ein.
                </p>

                <form onSubmit={handleVerificationSubmit} className="space-y-6" noValidate>
                  <div className="flex items-center justify-center gap-2 md:gap-3">
                    {verificationCode.map((digit, index) => (
                      <input
                        key={`verification-digit-${index}`}
                        ref={(el) => {
                          verificationRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleVerificationInputChange(index, e.target.value)}
                        onKeyDown={(e) => handleVerificationKeyDown(index, e)}
                        onPaste={(e) => handleVerificationPaste(index, e)}
                        aria-label={`Code-Ziffer ${index + 1}`}
                        className="w-11 h-14 md:w-12 md:h-16 rounded-xl border border-gray-300 text-center text-2xl font-black text-secondary focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
                      />
                    ))}
                  </div>

                  {errors.form && (
                      <div className="p-4 bg-primary/10 rounded-2xl text-[11px] text-primary font-bold border border-primary/20" role="alert">
                        {errors.form}
                      </div>
                  )}

                  <button
                    type="submit"
                    className="w-full bg-primary text-white py-4 rounded-2xl font-black text-lg hover:opacity-90 transition-all shadow-lg active:scale-[0.99] focus:ring-4 focus:ring-primary/30"
                  >
                    {isLoading ? 'Verarbeite...' : 'Code prüfen'}
                  </button>
                </form>

                <button
                  type="button"
                  onClick={() => {
                    setWaitlistSubmitted(false);
                    setVerificationCode(Array(6).fill(''));
                  }}
                  className="mt-5 w-full text-sm font-bold text-primary hover:underline focus:ring-2 focus:ring-primary rounded"
                >
                  Zurück zum Formular
                </button>
              </div>
            )}
            {codeSubmitted && (
              <div className="text-center py-12 animate-fade-in" role="status" aria-live="polite">
                <div className="bg-green-50 w-24 h-24 rounded-full flex items-center justify-center text-5xl mx-auto mb-8 text-green-600 shadow-inner" aria-hidden="true">✅</div>
                <h4 className="text-3xl font-black mb-4 text-secondary">Geschafft!</h4>
                <p className="text-lg text-gray-500 leading-relaxed mb-8 px-4">
                  Du bist ab jetzt auf der Warteliste für unsere App und erhälst immer die neuesten Informationen. Du kannst diese Seite nun verlassen.
                </p>
                <button 
                onClick={() => {setWaitlistSubmitted(false); setCodeSubmitted(false)}}
                className="text-sm font-bold text-primary hover:underline focus:ring-2 focus:ring-primary rounded"
                >
                  Zurück zum Formular
                </button>
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DownloadSection;
